import { useState, useEffect } from 'react';
import { useNavigate, Navigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth, getLandingPath } from '../lib/auth';
import { domainAPI } from '../lib/api';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Phone, MapPin, Clock, Shield, Heart, Activity, ChevronDown, Building2 } from 'lucide-react';
import { LoginMethods } from './LoginMethods';
import { TenantNotConfigured } from '../components/TenantNotConfigured';
import { canRedirectToSubdomain, getRootDomain } from '../lib/subdomain';

const KLICK_LOGO = '/assets/images/klick-niramaya-logo.png';
// v38.9 — Removed the sushrutahospital.org hot-link (the remote host blocks
// inter-origin fetches). When a tenant has no `branding.hero_image_url` set,
// the BrandedLogin renders without a hero image rather than showing a broken
// image icon. Super-admins can upload a hospital photo via the Branding page.
const HOSPITAL_BG = 'https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1920&q=80';

/**
 * v38.8 — Multi-tenant Login Page
 *
 * Three render modes, decided strictly by the browser host (not by query params):
 *   1. **Tenant-locked** — the SPA is loaded under a tenant subdomain
 *      (e.g. `prashanti.klickniramaya.com`). App.js has already verified the
 *      slug and persisted `tenantLocked=1` + `tenantSlug` in localStorage.
 *      We auto-fetch full branding via `/api/tenant/by-slug/<slug>` and render
 *      ONLY the BrandedLogin — no dropdown, no "Back to Platform Login",
 *      no cross-hospital switcher. Any `?hospital=` query param is stripped.
 *
 *   2. **Root-domain (production)** — `klickniramaya.com`, `www.klickniramaya.com`
 *      etc. Render the platform login with the hospital dropdown. On selection:
 *        • if hospital has `dns_configured=true` AND `slug` → redirect to
 *          `https://<slug>.<rootDomain>` (full page navigation).
 *        • otherwise → render the shared `<TenantNotConfigured>` page inline.
 *
 *   3. **Preview / localhost** — `*.preview.emergentagent.com` and similar.
 *      Keep the legacy `?hospital=<code>` query-param flow so QA can still
 *      exercise per-hospital branded logins without DNS.
 */
export const LoginPage = () => {
  const [hospitalData, setHospitalData] = useState(null);
  const [hospitalList, setHospitalList] = useState([]);
  const [loadingBranding, setLoadingBranding] = useState(true);
  const [showSelector, setShowSelector] = useState(false);
  const [errorParam, setErrorParam] = useState('');
  const [notConfiguredHospital, setNotConfiguredHospital] = useState(null);

  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const hospitalSlug = searchParams.get('hospital');
  const queryError = searchParams.get('error');

  // ── Tenant lock detection (set by App.js after /api/tenant/by-slug/) ──
  const tenantLocked = typeof window !== 'undefined' && localStorage.getItem('tenantLocked') === '1';
  const lockedTenantSlug = typeof window !== 'undefined' ? localStorage.getItem('tenantSlug') : null;

  useEffect(() => {
    loadBranding();
    if (queryError) {
      setErrorParam(queryError);
      setTimeout(() => {
        searchParams.delete('error');
        setSearchParams(searchParams, { replace: true });
      }, 100);
    }
    // v38.8 — On a tenant subdomain we must never expose the `?hospital=<code>`
    // query param: it leaks the underlying platform mechanism and breaks the
    // brand promise that the hospital "owns" this URL. Strip it silently.
    if (tenantLocked && hospitalSlug) {
      searchParams.delete('hospital');
      setSearchParams(searchParams, { replace: true });
    }
  }, [hospitalSlug]);

  const loadBranding = async () => {
    setLoadingBranding(true);
    try {
      // Tenant-locked: ignore the dropdown entirely, just fetch this tenant's
      // full branding payload from the public tenant-bootstrap endpoint.
      if (tenantLocked && lockedTenantSlug) {
        const base = process.env.REACT_APP_BACKEND_URL;
        const { data } = await axios.get(`${base}/api/tenant/by-slug/${lockedTenantSlug}`);
        setHospitalData(data);
        setHospitalList([]); // never render the dropdown under a tenant
        return;
      }
      // Root / preview: fetch the full hospital list so the dropdown can render.
      const listRes = await domainAPI.listHospitals();
      setHospitalList(listRes.data || []);
      if (hospitalSlug) {
        const res = await domainAPI.resolve(hospitalSlug);
        setHospitalData(res.data?.found ? res.data.hospital : null);
      } else {
        setHospitalData(null);
      }
    } catch (err) {
      console.error('Failed to load branding:', err);
      setHospitalData(null);
    } finally {
      setLoadingBranding(false);
    }
  };

  if (user) {
    return <Navigate to={getLandingPath(user)} replace />;
  }

  // v38.8 — Root-domain dropdown selection handler.
  //   • DNS configured (production): redirect to https://<slug>.<rootDomain>
  //   • DNS not configured (production): render <TenantNotConfigured> inline
  //   • Preview / localhost: legacy ?hospital=<code> query-param flow
  const selectHospital = (hospital) => {
    setShowSelector(false);
    if (canRedirectToSubdomain()) {
      const root = getRootDomain();
      if (hospital.dns_configured && hospital.slug && root) {
        window.location.href = `https://${hospital.slug}.${root}`;
        return;
      }
      // Production but DNS not yet live for this tenant — show the shared
      // "Hospital workspace not configured" page rather than redirecting to
      // a domain the browser cannot resolve.
      setNotConfiguredHospital(hospital);
      return;
    }
    // Preview / localhost fall-through (used by QA + Emergent preview):
    setSearchParams({ hospital: hospital.code });
  };

  // ── Render: tenant not-configured (root-domain dropdown picked a no-DNS hospital) ──
  if (notConfiguredHospital) {
    return (
      <TenantNotConfigured
        slug={notConfiguredHospital.slug || notConfiguredHospital.code}
        hostname={window.location.hostname}
        hospitalName={notConfiguredHospital.name}
        onContinue={() => setNotConfiguredHospital(null)}
      />
    );
  }

  // ── Render: tenant-locked → only branded login, no switcher ──
  if (tenantLocked) {
    if (loadingBranding || !hospitalData) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="login-page-loading">
          <div className="h-10 w-10 rounded-full border-2 border-slate-200 border-t-teal-600 animate-spin" />
        </div>
      );
    }
    return (
      <BrandedLogin
        hospital={hospitalData}
        hospitalList={[]}            // hide cross-hospital switcher
        showSelector={false}
        setShowSelector={() => {}}
        selectHospital={() => {}}
        setSearchParams={setSearchParams}
        errorParam={errorParam}
        tenantLocked
      />
    );
  }

  // ── Render: root-domain — explicit ?hospital=<code> still works for preview/QA ──
  if (hospitalData) {
    return (
      <BrandedLogin
        hospital={hospitalData}
        hospitalList={hospitalList}
        showSelector={showSelector}
        setShowSelector={setShowSelector}
        selectHospital={selectHospital}
        setSearchParams={setSearchParams}
        errorParam={errorParam}
        tenantLocked={false}
      />
    );
  }

  // ── Render: default platform login with dropdown ──
  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* Left Panel - Platform Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src={HOSPITAL_BG} alt="Hospital" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(145deg, rgba(13,148,136,0.85) 0%, rgba(5,80,72,0.92) 100%)' }} />
        <div className="relative z-10 flex flex-col justify-between h-full w-full px-12 py-10">
          <div>
            <img src={KLICK_LOGO} alt="Klick Niramaya HMS" className="h-72 w-auto rounded-lg p-2" data-testid="login-logo" />
            <h1 className="text-4xl sm:text-5xl font-bold text-white mt-8 leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Klick Niramaya HMS
            </h1>
            <p className="text-lg text-emerald-100/80 mt-4 max-w-md leading-relaxed">
              Cloud-based Hospital Management System. Multi-tenant, multi-specialty healthcare platform for modern hospitals.
            </p>
          </div>

          {/* Hospital Cards */}
          {hospitalList.length > 0 && (
            <div className="mt-auto">
              <p className="text-xs uppercase tracking-wider text-emerald-200/60 font-semibold mb-3">Our Hospital Partners</p>
              <div className="grid grid-cols-2 gap-3">
                {hospitalList.filter(h => h.logo_url).map(h => (
                  <button
                    key={h.id}
                    onClick={() => selectHospital(h)}
                    className="flex items-center gap-3 p-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 transition-all text-left group"
                    data-testid={`hospital-card-${h.code}`}
                  >
                    <img src={h.logo_url} alt={h.name} className="h-10 w-10 rounded-lg object-contain bg-white/90 p-1" />
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium truncate group-hover:text-emerald-200 transition-colors">{h.name}</p>
                      <p className="text-xs text-emerald-300/60">{h.city}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 bg-slate-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            <img src={KLICK_LOGO} alt="Klick Niramaya HMS" className="h-20 w-auto rounded-lg" />
          </div>

          {/* Hospital Selector (mobile) */}
          <div className="lg:hidden mb-6">
            <HospitalSelectorDropdown
              hospitalList={hospitalList}
              showSelector={showSelector}
              setShowSelector={setShowSelector}
              selectHospital={selectHospital}
            />
          </div>

          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden">
            <div className="h-1.5 w-full bg-gradient-to-r from-teal-600 via-emerald-500 to-lime-500" />
            <div className="px-8 pt-8 pb-2">
              <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="login-title">
                Welcome Back
              </h2>
              <p className="text-sm text-slate-500 mt-1">Sign in to the HMS platform</p>
            </div>

            <div className="px-8 pb-8 pt-4">
              {/* Desktop Hospital Selector */}
              <div className="hidden lg:block mb-5">
                <HospitalSelectorDropdown
                  hospitalList={hospitalList}
                  showSelector={showSelector}
                  setShowSelector={setShowSelector}
                  selectHospital={selectHospital}
                />
              </div>

              {errorParam && (
                <Alert variant="destructive" className="rounded-xl mb-4">
                  <AlertDescription>{errorParam}</AlertDescription>
                </Alert>
              )}

              <LoginMethods theme="default" />
            </div>
          </div>
          <p className="mt-6 text-center text-xs text-slate-400">
            &copy; {new Date().getFullYear()} Klick Niramaya HMS by KlickApps. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

/* ========== Hospital Selector Dropdown ========== */
const HospitalSelectorDropdown = ({ hospitalList, showSelector, setShowSelector, selectHospital }) => {
  if (!hospitalList.length) return null;
  return (
    <div className="relative" data-testid="hospital-selector-dropdown">
      <button
        type="button"
        onClick={() => setShowSelector(!showSelector)}
        className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50 hover:bg-slate-100/80 transition-all text-left"
      >
        <div className="flex items-center gap-2.5">
          <Building2 className="h-4 w-4 text-slate-400" />
          <span className="text-sm text-slate-600">Select a hospital portal</span>
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${showSelector ? 'rotate-180' : ''}`} />
      </button>
      {showSelector && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-xl shadow-xl shadow-slate-200/60 max-h-64 overflow-y-auto">
          {hospitalList.map(h => (
            <button
              key={h.id}
              onClick={() => selectHospital(h)}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors text-left border-b border-slate-50 last:border-0"
              data-testid={`hospital-option-${h.code}`}
            >
              {h.logo_url ? (
                <img src={h.logo_url} alt={h.name} className="h-8 w-8 rounded-lg object-contain bg-slate-100 p-0.5" />
              ) : (
                <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-slate-400" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">{h.name}</p>
                {h.city && <p className="text-xs text-slate-400">{h.city}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ========== Branded Hospital Login ========== */
// v38.10 — Per-tenant local hero image registry. Used as a self-healing fallback
// when the DB's `branding.hero_image_url` either (a) is empty, or (b) points at a
// remote URL that fails to load (e.g. the legacy sushrutahospital.org hot-link or
// the customer-assets URL blocked by a strict referrer policy on a custom domain).
//
// Files live in `/app/frontend/public/assets/images/<slug>-hero.<ext>` and are
// served same-origin by the SPA — no CORS, no referrer issues, ever.
const LOCAL_HERO_BY_SLUG = {
  prashanti: '/assets/images/prashanti-hero.png',
};

const BrandedLogin = ({
  hospital,
  hospitalList, showSelector, setShowSelector, selectHospital, setSearchParams,
  errorParam,
  tenantLocked = false,
}) => {
  const primaryColor = hospital.primary_color || '#0B1D3A';
  const secondaryColor = hospital.secondary_color || '#00897B';
  const accentColor = hospital.accent_color || '#26C6DA';

  // Pick the best available hero source: local-by-slug first (guaranteed to work
  // same-origin), then DB-configured URL, then null (don't render the <img>).
  const localFallback = LOCAL_HERO_BY_SLUG[hospital.slug] || null;
  const initialHero = localFallback || hospital.hero_image_url || null;
  const [heroSrc, setHeroSrc] = useState(initialHero);
  const [heroBroken, setHeroBroken] = useState(false);

  // v38.10 — Self-healing image src. If the configured src fails to load,
  // swap to the local fallback once; if that ALSO fails, hide the slot.
  const handleHeroError = () => {
    if (heroSrc !== localFallback && localFallback) {
      setHeroSrc(localFallback);
    } else {
      setHeroBroken(true);
    }
  };

  const lightAccent = accentColor + '18';

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" data-testid="login-page">
      {/* Left Panel - Hospital Branding */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden" style={{ background: `linear-gradient(145deg, ${primaryColor} 0%, ${secondaryColor}dd 100%)` }}>
        {/* Pattern overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
        {/* Glow effects */}
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full opacity-10" style={{ background: `radial-gradient(circle, ${accentColor} 0%, transparent 70%)` }} />
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-10" style={{ background: `radial-gradient(circle, ${accentColor} 0%, transparent 70%)` }} />

        <div className="relative z-10 flex flex-col justify-between h-full w-full px-12 py-10">
          {/* Top - Logo */}
          <div>
            {hospital.logo_url && (
              <img src={hospital.logo_url} alt={hospital.name} className="h-20 w-auto drop-shadow-2xl" data-testid="login-logo" />
            )}
            <div className="mt-8 max-w-lg">
              {hospital.tagline ? (
                <>
                  <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {hospital.tagline.split(',')[0]}
                  </h1>
                  {hospital.tagline.includes(',') && (
                    <h2 className="text-2xl sm:text-3xl font-semibold mt-1" style={{ color: accentColor, fontFamily: 'Manrope, sans-serif' }}>
                      {hospital.tagline.split(',').slice(1).join(',').trim()}
                    </h2>
                  )}
                </>
              ) : (
                <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {hospital.name}
                </h1>
              )}
              {hospital.address && (
                <p className="mt-4 text-base text-slate-300/80 leading-relaxed max-w-md">
                  {hospital.address}{hospital.city ? `, ${hospital.city}` : ''}{hospital.state ? `, ${hospital.state}` : ''}
                </p>
              )}
            </div>
          </div>

          {/* Middle image — only render when a hero source is available and
              loads successfully. If both the configured URL and the local
              fallback fail, the slot collapses cleanly (no broken-icon ghost). */}
          {heroSrc && !heroBroken && (
            <div className="flex-1 flex items-end justify-center pt-8 pb-4">
              <div className="relative w-full max-w-2xl">
                <div className="absolute inset-0 rounded-2xl" style={{ background: `linear-gradient(to top, ${primaryColor} 0%, transparent 40%)` }} />
                <img
                  src={heroSrc}
                  alt="Medical Team"
                  className="w-full rounded-2xl object-cover shadow-2xl"
                  style={{ maxHeight: '640px', objectPosition: 'top' }}
                  onError={handleHeroError}
                  data-testid="branded-hero-image"
                />
              </div>
            </div>
          )}

          {/* Bottom stats */}
          <div className="grid grid-cols-4 gap-4 pt-4 border-t border-white/10">
            {[
              { icon: Shield, label: 'Departments', value: '10+' },
              { icon: Heart, label: 'Surgeries', value: '5000+' },
              { icon: Activity, label: 'Emergency', value: '24/7' },
              { icon: Clock, label: 'Years', value: '15+' },
            ].map(s => (
              <div key={s.label} className="text-center">
                <s.icon className="h-4 w-4 mx-auto mb-1.5" style={{ color: accentColor }} />
                <p className="text-lg font-bold text-white">{s.value}</p>
                <p className="text-[11px] text-slate-400 uppercase tracking-wider">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10" style={{ background: `linear-gradient(180deg, ${lightAccent} 0%, #f8fafc 50%, #f0f9ff 100%)` }}>
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex flex-col items-center mb-8">
            {hospital.logo_url && <img src={hospital.logo_url} alt={hospital.name} className="h-16 w-auto mb-3" />}
            <p className="text-sm font-medium" style={{ color: primaryColor }}>Hospital Management System</p>
          </div>

          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/60 border border-slate-100 overflow-hidden">
            <div className="h-1.5 w-full" style={{ background: `linear-gradient(90deg, ${primaryColor} 0%, ${secondaryColor} 50%, ${accentColor} 100%)` }} />
            <div className="px-8 pt-8 pb-2">
              <h2 className="text-2xl font-bold" style={{ color: primaryColor, fontFamily: 'Manrope, sans-serif' }} data-testid="login-title">
                Welcome Back
              </h2>
              <p className="text-sm text-slate-500 mt-1">Sign in to access <span className="font-medium" style={{ color: secondaryColor }}>{hospital.name}</span></p>
            </div>

            <div className="px-8 pb-8 pt-4">
              {errorParam && (
                <Alert variant="destructive" className="rounded-xl mb-4">
                  <AlertDescription>{errorParam}</AlertDescription>
                </Alert>
              )}
              <LoginMethods theme="branded" primaryColor={primaryColor} secondaryColor={secondaryColor} accentColor={accentColor} />
            </div>
          </div>

          {/* v38.8 — Switcher block. Hidden when the tab is tenant-locked
              (i.e. loaded under prashanti.klickniramaya.com): the brand promise
              is that this URL IS Prashanti's portal, so cross-tenant nav must
              not appear. Only the legacy `?hospital=<code>` flow (preview/QA)
              still shows the switcher. */}
          {!tenantLocked && (
            <div className="mt-5 flex items-center justify-center gap-3" data-testid="branded-login-switcher">
              <button
                onClick={() => setSearchParams({})}
                className="text-xs font-medium hover:underline transition-colors"
                style={{ color: secondaryColor }}
                data-testid="back-to-platform-btn"
              >
                Back to Platform Login
              </button>
              {hospitalList.filter(h => h.code !== hospital.code).length > 0 && (
                <span className="text-slate-300">|</span>
              )}
              {hospitalList.filter(h => h.code !== hospital.code).slice(0, 2).map(h => (
                <button
                  key={h.id}
                  onClick={() => selectHospital(h)}
                  className="text-xs text-slate-500 hover:text-slate-700 hover:underline transition-colors"
                >
                  {h.name}
                </button>
              ))}
            </div>
          )}

          {/* Contact & Footer */}
          {(hospital.phone || hospital.city) && (
            <div className="mt-4 flex items-center justify-center gap-6 text-xs text-slate-400">
              {hospital.phone && <span className="flex items-center gap-1.5"><Phone className="h-3 w-3" /> {hospital.phone}</span>}
              {hospital.city && <span className="flex items-center gap-1.5"><MapPin className="h-3 w-3" /> {hospital.city}{hospital.state ? `, ${hospital.state}` : ''}</span>}
            </div>
          )}
          <p className="mt-3 text-center text-xs text-slate-400">
            &copy; {new Date().getFullYear()} {hospital.name}. Powered by <span className="font-semibold text-teal-600">Klick Niramaya HMS</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
